import React, { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { NavigateHeader } from '@/components/atoms/navigate-header'
import { Layout } from '@/components/template/layout'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { SpacePaymentForm } from '@/components/organisms/space/space-payment-form'
import { PaymentFormTemplate } from '@/components/template/payment-form-template'
import { usePickByLanguage } from '@/hooks/use-pick-by-language'
import { globalHotelId } from '@/hooks/use-hotel-id'
import dayjs from 'dayjs'
import { FormLayout } from '@/components/template/form-layout'
import { Divider } from '@/components/atoms/divider'
import { css } from '@emotion/react'
import { displayYen } from '@/libs/text'
import { TotalAmount } from '@/components/atoms/total-amount'
import { ContentWrapper } from '@/components/template/content-wrapper'
import { fetchSpaceEstimatePrice, buildEstimatePriceRequest } from '@/features/space/space-estimate-price-slice'
import { clearSpaceFormPrice } from '@/features/space/spaces-slice'
import { MessageType, toastAction } from '@/features/notification/toast-slice'

export const SpaceConfirmation: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const pickByLanguage = usePickByLanguage()

  const { spaceId } = useParams<{ spaceId: string }>()

  const { spaceDetails, formValues } = useAppSelector(state => state.spaces)
  const reservationInfo = useAppSelector(state => state.spaceReservation.reservationInfo)
  const spaceInfo = useMemo(() => spaceDetails.find(detail => detail.id === spaceId), [spaceDetails])

  useEffect(() => {
    return () => {
      dispatch(clearSpaceFormPrice())
    }
  }, [dispatch])

  // 確認画面表示時に1回だけ見積もりを取得。formValues を依存に含めると
  // fetch 成功で formValues が更新され再実行されるため、意図的にマウント時のみ実行。
  useEffect(() => {
    const hotelId = globalHotelId()
    const request =
      formValues && hotelId
        ? buildEstimatePriceRequest(formValues, hotelId, reservationInfo?.reservationId)
        : null
    if (!request) return
    dispatch(fetchSpaceEstimatePrice(request))
      .unwrap()
      .catch(() => {
        dispatch(toastAction.setMessage(MessageType.Error, t('Failed to load estimate price')))
      })
  }, [])

  const createDisplayTime = (props: { hour: string; minute: string }) => {
    return `${props.hour}:${props.minute}`
  }

  return (
    <Layout>
      <NavigateHeader title={t('Payment details')} style={{ marginBottom: '1rem' }} />
      <ContentWrapper>
        {spaceInfo && formValues && (
          <FormLayout>
            <h3 css={spaceTypeNameStyle}>{pickByLanguage(spaceInfo.title)}</h3>
            <p css={usageInfoStyle}>
              {`${dayjs(formValues.useDay).format('YYYY/MM/DD')}
              ${createDisplayTime({
                hour: formValues.usageFromHour,
                minute: formValues.usageFromMinute,
              })}~${createDisplayTime({ hour: formValues.usageToHour, minute: formValues.usageToMinute })} - ${
                formValues.quantity
              }${pickByLanguage(spaceInfo?.stockUnit)}`}
            </p>
            <p css={priceStyle}>{formValues.subtotalAmount !== undefined ? displayYen(formValues.subtotalAmount) : '-'}</p>

            <Divider customCss={{ marginBlock: 16 }} />

            <div css={usageStyle}>
              <p>
                {t('Requests from Facilities')}：{formValues.answer || '-'}
              </p>
              <p>
                {`${t('Number of users')}：
                ${formValues.pax}
                ${t('Guest')}`}
              </p>
            </div>
          </FormLayout>
        )}
        {formValues?.totalAmount !== undefined && <TotalAmount {...formValues} />}
        <PaymentFormTemplate>
          <SpacePaymentForm />
        </PaymentFormTemplate>
      </ContentWrapper>
    </Layout>
  )
}

const spaceTypeNameStyle = css({
  fontSize: 16,
  fontWeight: 'bold',
})

const usageInfoStyle = css({
  marginTop: 8,
  marginBottom: 12,
  fontSize: 12,
})

const priceStyle = css({
  fontSize: 14,
  fontWeight: 'bold',
})

const usageStyle = css({
  p: {
    fontSize: 12,
    lineHeight: 1.75,
  },
})
